import { useEffect, useRef, useState } from 'react'
import { NavLink, useNavigate } from 'react-router'
import type { JSX } from 'react'
import { useMutation } from '@/api/query'
import { DEFAULT_HOME } from '@/auth/routes'
import { initials } from '@/lib/format'
import { useTenants } from '@/tenant/context'
import { InlineError } from '@/ui/states'

/**
 * The organisation switcher, in the top bar.
 *
 * The owner's decision of 2026-09-10: *"org shift should happen after logging
 * and selecting from the top right corner, also I should have option to change
 * the default org."*
 *
 * **What this replaced.** Switching used to mean the account menu's *Switch
 * organisation* item, which navigated to `/organisations` — a full-page
 * interstitial outside the shell, with the sidebar and the screen you were on
 * both thrown away, to change one value. That page still exists and is still
 * linked from here, but as somewhere you go to *manage* organisations rather
 * than as the toll gate every switch had to pass through.
 *
 * **A dedicated control rather than a second submenu under the avatar** (the
 * owner chose this directly). The avatar menu answers *who am I and how do I
 * leave*; this answers *which organisation am I in*, and the second question is
 * asked far more often by the agency owners this feature exists for. Putting it
 * behind the avatar would also have hidden the current organisation's name,
 * which is half of what the control is for — the button names it, so the answer
 * needs no click at all.
 *
 * **Every switch lands on the dashboard** (the owner chose this over staying
 * put). Keeping the route would 404 or 403 on anything record-scoped —
 * `/seo/sites/<id>` names a site that exists in one organisation and not the
 * other — and mapping "the equivalent screen over there" is a guess that is
 * wrong exactly when it matters. `DEFAULT_HOME` is somewhere every organisation
 * has.
 *
 * The dropdown closes on Escape and on a click anywhere else, matching
 * `AccountMenu` — see its note on why the second is the one people actually hit.
 */
export function OrgSwitcher(): JSX.Element | null {
  const { memberships, active, defaultTenantId, select, setDefault } = useTenants()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const wrapper = useRef<HTMLDivElement>(null)

  const switching = useMutation((tenantId: string) => select(tenantId))
  const defaulting = useMutation((tenantId: string) => setDefault(tenantId))

  useEffect(() => {
    if (!open) return
    function onDocument(event: MouseEvent | KeyboardEvent): void {
      if (event instanceof KeyboardEvent) {
        if (event.key === 'Escape') setOpen(false)
        return
      }
      if (event.target instanceof Node && !wrapper.current?.contains(event.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocument)
    document.addEventListener('keydown', onDocument)
    return () => {
      document.removeEventListener('mousedown', onDocument)
      document.removeEventListener('keydown', onDocument)
    }
  }, [open])

  // Nothing to switch between and nothing to name. The shell sits behind
  // `RequireTenant`, so this is unreachable in the product — it is here because
  // a component that throws on a null the type system permits is a component
  // that fails in a test harness rather than at the boundary that guarantees it.
  if (!active) return null

  const busy = switching.pending || defaulting.pending

  return (
    <div className="app-orgmenu" ref={wrapper}>
      <button
        type="button"
        className="ar-btn ar-btn--ghost ar-btn--sm app-orgmenu__button"
        aria-haspopup="menu"
        aria-expanded={open}
        // The visible text first, then the action — WCAG 2.5.3, Label in Name.
        aria-label={`${active.tenant.name}. Switch organisation`}
        onClick={() => {
          setOpen((current) => !current)
        }}
      >
        {/* The initials are drawn by CSS (`::before` from `data-initials`), not
            written as text. axe counts text inside a control as its visible
            label even under `aria-hidden`, so "KI" as a child made the visible
            label "KI Kavya Interiors" and no sensible accessible name could
            contain it (`label-content-name-mismatch`, every shell screen). A
            pseudo-element is not text, so the label is the name alone. */}
        <span
          className="ar-avatar ar-avatar--sm app-orgmenu__avatar"
          data-initials={initials(active.tenant.name)}
          aria-hidden="true"
        />
        {/* `aria-hidden`, because the button's own label already names the
            organisation — without it a screen reader reads the name twice, once
            as part of "Organisation: Kavya Interiors" and again as the text
            inside. Hidden below 1100px by the stylesheet, where the top bar has
            no room for it and the label carries the whole answer. */}
        <span className="app-orgmenu__name" aria-hidden="true">
          {active.tenant.name}
        </span>
        <span className="app-orgmenu__caret" aria-hidden="true">
          ▾
        </span>
      </button>

      {open ? (
        <div className="app-orgmenu__panel" role="menu" aria-label="Your organisations">
          <div className="app-orgmenu__head">Switch organisation</div>

          {/* Both mutations report here rather than each row carrying its own.
              A failure is about the action just taken, there is only ever one in
              flight, and a message pinned to a row the user may already have
              scrolled past is a message they will not find (WEB-9).

              **Each handler resets the other before it runs**, and without that
              this line is a WEB-9 defect of its own rather than a fix for one.
              `useMutation` clears only its *own* failure on `run`, so a switch
              that failed left its message standing over every later action —
              including a star press that succeeded, and, because `??` falls
              through only on null, including a later *setDefault* refusal whose
              message it hid behind the older one. The reset makes the panel
              report the action just taken, which is what it claims to do. */}
          <InlineError failure={switching.state.failure ?? defaulting.state.failure} />

          <div className="app-orgmenu__list">
            {memberships.map((row) => {
              const current = row.tenant.id === active.tenant.id
              const isDefault = row.tenant.id === defaultTenantId
              return (
                // `role="none"` so the row itself is not announced as an item:
                // it holds two, and a menu that claims otherwise makes the star
                // unreachable by keyboard in the order it appears.
                <div className="app-orgmenu__row" role="none" key={row.tenant.id}>
                  <button
                    type="button"
                    role="menuitem"
                    className="app-orgmenu__pick"
                    disabled={busy}
                    aria-current={current ? 'true' : undefined}
                    onClick={() => {
                      if (current) {
                        setOpen(false)
                        return
                      }
                      defaulting.reset()
                      void (async () => {
                        const result = await switching.run(row.tenant.id)
                        if (!result.ok) return
                        setOpen(false)
                        void navigate(DEFAULT_HOME)
                      })()
                    }}
                  >
                    <span className="ar-avatar ar-avatar--sm" aria-hidden="true">
                      {initials(row.tenant.name)}
                    </span>
                    <span className="app-orgmenu__body">
                      <strong>{row.tenant.name}</strong>
                      <span>
                        {row.membership.role}
                        {isDefault ? ' · Default' : ''}
                      </span>
                    </span>
                    {current ? (
                      <span className="ar-badge ar-badge--success app-orgmenu__badge">Current</span>
                    ) : null}
                  </button>

                  {/* The default control. A star rather than a menu row of its
                      own, because it is a property *of* the organisation beside
                      it — twelve organisations would otherwise be twenty-four
                      menu items, half of them repeating the other half's name.

                      Disabled on the one that already is the default rather than
                      hidden: a control that vanishes from the row it applies to
                      is one the user has to re-find by elimination, and the
                      pressed star is what says which row that is. */}
                  <button
                    type="button"
                    role="menuitem"
                    className="app-orgmenu__star"
                    disabled={busy || isDefault}
                    aria-pressed={isDefault}
                    aria-label={
                      isDefault
                        ? `${row.tenant.name} is where you land when you sign in`
                        : `Land in ${row.tenant.name} when you sign in`
                    }
                    onClick={() => {
                      switching.reset()
                      void defaulting.run(row.tenant.id)
                    }}
                  >
                    <span aria-hidden="true">{isDefault ? '★' : '☆'}</span>
                  </button>
                </div>
              )
            })}
          </div>

          <div className="app-orgmenu__foot">
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false)
                // Router state rather than a query string: it is a one-shot
                // instruction to the next screen, not part of the address. A
                // `?new=1` left in the bar would re-open the form on every
                // reload and on every back-navigation to the page.
                void navigate('/organisations', { state: { create: true } })
              }}
            >
              New organisation
            </button>
            <NavLink
              to="/organisations"
              role="menuitem"
              onClick={() => {
                setOpen(false)
              }}
            >
              Manage organisations
            </NavLink>
          </div>
        </div>
      ) : null}
    </div>
  )
}
